import { Skeleton } from '@/components/ui/skeleton';
import type { DataGridColumnDef } from '@/components/ui/data-grid';
import { cn } from '@/lib/utils';

type ColumnGap = 'none' | 'xs' | 'sm' | 'md' | 'lg';

export type DataGridSkeletonProps<T> = {
  /**
   * Same column definitions passed to DataGrid — used to match column tracks and
   * labels. Omit for a generic table shape and use `colCount` instead.
   */
  columns?: DataGridColumnDef<T>[];
  /** Generic column count when `columns` is not provided. Defaults to 4. */
  colCount?: number;
  /** Number of placeholder rows to render. */
  rows?: number;
  /** Reserve a leading selection-checkbox column (matches DataGrid `selectable`). */
  selectable?: boolean;
  /** Reserve a trailing actions column (matches DataGrid `actions`). */
  actions?: boolean;
  /** Match DataGrid `compact` row spacing. */
  compact?: boolean;
  /** Match DataGrid `columnGap`. */
  columnGap?: ColumnGap;
  /** Match DataGrid `card` presentation (skips the bordering wrapper). */
  card?: boolean;
  className?: string;
};

const GAP_CLASS: Record<ColumnGap, string> = {
  none: 'gap-0',
  xs: 'gap-1',
  sm: 'gap-2',
  md: 'gap-4',
  lg: 'gap-6',
};

// Deterministic per-column cell widths so rows look organic without randomness.
const CELL_WIDTHS = ['w-3/4', 'w-1/2', 'w-2/3', 'w-2/5', 'w-4/5', 'w-1/3'];

function columnTemplate<T>(col: DataGridColumnDef<T>): string {
  if (col.width) return col.width.trim();
  if (col.minWidth && col.maxWidth)
    return `minmax(${col.minWidth}px, ${col.maxWidth}px)`;
  if (col.minWidth) return `minmax(${col.minWidth}px, 1fr)`;
  if (col.maxWidth) return `minmax(0, ${col.maxWidth}px)`;
  return '1fr';
}

/**
 * Loading placeholder for DataGrid. Renders the real column headers plus shaped
 * skeleton rows that track the same column widths, so a table settles into its
 * data without a spinner or layout shift. Pass the same `columns`/`selectable`/
 * `actions`/`compact`/`columnGap` you pass to DataGrid for an aligned result.
 */
export function DataGridSkeleton<T>({
  columns,
  colCount = 4,
  rows = 8,
  selectable = false,
  actions = false,
  compact = false,
  columnGap = 'md',
  card = false,
  className,
}: DataGridSkeletonProps<T>) {
  // Fall back to a generic, label-less set of columns when none are provided.
  const effectiveColumns: DataGridColumnDef<T>[] =
    columns && columns.length > 0
      ? columns
      : Array.from({ length: colCount }, (_, i) => ({
          key: i as unknown as keyof T,
          label: '',
        }));
  const dataTracks = effectiveColumns.map(columnTemplate);
  const hasFlexibleTrack = dataTracks.some((track) => track.includes('1fr'));
  const needsActionsSpacer = actions && !hasFlexibleTrack;

  const gridTemplateColumns = [
    selectable ? '24px' : null,
    ...dataTracks,
    needsActionsSpacer ? '1fr' : null,
    actions ? '80px' : null,
  ]
    .filter(Boolean)
    .join(' ');

  const gapClass = GAP_CLASS[columnGap];
  const rowPadding = compact ? 'px-4 py-2' : 'px-4 py-3';

  return (
    <div
      className={cn(!card && 'border-border rounded-lg border', className)}
      aria-hidden="true"
    >
      {/* Skeleton rows (no header — the grid fills its header in once loaded) */}
      <div className="divide-border/50 divide-y">
        {Array.from({ length: rows }).map((_, rowIdx) => (
          <div
            key={rowIdx}
            className={cn('grid items-center', gapClass, rowPadding)}
            style={{ gridTemplateColumns }}
          >
            {selectable && <Skeleton className="h-4 w-4 rounded-sm" />}
            {effectiveColumns.map((column, colIdx) => (
              <div
                key={colIdx}
                className={cn(
                  'min-w-0',
                  column.align === 'center' && 'flex justify-center',
                  column.align === 'right' && 'flex justify-end'
                )}
              >
                <Skeleton
                  className={cn(
                    'h-4',
                    CELL_WIDTHS[(rowIdx + colIdx) % CELL_WIDTHS.length]
                  )}
                />
              </div>
            ))}
            {needsActionsSpacer && <div aria-hidden="true" />}
            {actions && (
              <div className="justify-self-end">
                <Skeleton className="h-7 w-7 rounded-md" />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
